import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { playerAPI } from '../../../services/api';
import { apiErrorMessage, unwrapApiBody } from '../../../utils/apiResponse';

const Box = styled.div`
  margin-top: 0.5rem;
  padding: 1rem 1.1rem;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: #fff;
`;

const Caution = styled.div`
  margin: 0 0 1rem;
  padding: 0.85rem 1rem;
  border-radius: 10px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  font-size: 0.9rem;
  line-height: 1.5;
`;

const Summary = styled.p`
  margin: 0 0 1rem;
  font-size: 0.9rem;
  line-height: 1.5;
  color: #334155;
`;

const Field = styled.label`
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  color: #043663;
`;

const PasswordInput = styled.input`
  display: block;
  width: 100%;
  max-width: 280px;
  margin-top: 0.4rem;
  padding: 0.6rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
`;

const ForfeitBtn = styled.button`
  margin-top: 1rem;
  padding: 0.7rem 1.25rem;
  border: none;
  border-radius: 8px;
  background: #b91c1c;
  color: #fff;
  font-weight: 700;
  cursor: pointer;
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const Muted = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: #64748b;
  line-height: 1.45;
`;

/**
 * @param {{ teamContext?: { teamId?: string, teamName?: string } }} props
 */
export default function HelpSeasonForfeitPanel({ teamContext }) {
  const { t } = useTranslation();
  const teamId = teamContext?.teamId;
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [password, setPassword] = useState('');
  const [preview, setPreview] = useState(null);
  const [done, setDone] = useState(false);

  const load = useCallback(async () => {
    if (!teamId) {
      setPreview(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = unwrapApiBody(await playerAPI.getSeasonForfeitPreview(teamId));
      setPreview(res);
      setDone(false);
    } catch (e) {
      const body = e?.response?.data;
      if (body && typeof body === 'object') {
        setPreview(body);
      } else {
        const msg = apiErrorMessage(e, t('phase2.playerHelpWizard.seasonForfeit.loadError'));
        setPreview({ success: false, message: msg });
      }
    } finally {
      setLoading(false);
    }
  }, [teamId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleForfeit = async () => {
    if (!teamId || !password.trim()) {
      toast.error(t('phase2.playerHelpWizard.seasonForfeit.passwordRequired'));
      return;
    }
    setSubmitting(true);
    try {
      const res = unwrapApiBody(await playerAPI.executeSeasonForfeit(teamId, { password }));
      if (res?.success !== false) {
        toast.success(t('phase2.playerHelpWizard.seasonForfeit.success'));
        setDone(true);
        setPassword('');
        await load();
      } else {
        toast.error(res?.message || t('phase2.playerHelpWizard.seasonForfeit.submitError'));
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, t('phase2.playerHelpWizard.seasonForfeit.submitError')));
    } finally {
      setSubmitting(false);
    }
  };

  if (!teamId) {
    return <Muted>{t('phase2.playerHelpWizard.pickTeamFirst')}</Muted>;
  }

  if (loading) {
    return <Muted>{t('phase2.playerHelpWizard.seasonForfeit.loading')}</Muted>;
  }

  if (preview?.already_forfeited || done) {
    return (
      <Box>
        <Muted>{t('phase2.playerHelpWizard.seasonForfeit.doneHint')}</Muted>
      </Box>
    );
  }

  if (preview?.success === false) {
    return (
      <Box>
        <Muted>{preview.message || t('phase2.playerHelpWizard.seasonForfeit.unavailable')}</Muted>
      </Box>
    );
  }

  const teamLabel = preview?.team_name || teamContext?.teamName || '';
  const seasonLabel = preview?.season_name || '';
  const matchCount = preview?.remaining_matches ?? 0;
  const estimate = preview?.rematch_estimate;
  const replacements = estimate?.expected_replacements ?? 0;
  const duplicates = estimate?.expected_duplicates ?? 0;
  const awards = estimate?.expected_awards ?? 0;

  return (
    <Box>
      <Caution>{t('phase2.playerHelpWizard.seasonForfeit.caution')}</Caution>
      <Summary>
        {t('phase2.playerHelpWizard.seasonForfeit.summary', {
          team: teamLabel,
          season: seasonLabel,
          count: matchCount,
        })}
      </Summary>
      {matchCount > 0 ? (
        <>
          <Muted>{t('phase2.playerHelpWizard.seasonForfeit.scoreHint')}</Muted>
          {estimate && (
            <Muted style={{ marginTop: '0.65rem' }}>
              {t('phase2.playerHelpWizard.seasonForfeit.rematchEstimate', {
                replacements,
                duplicatesClause:
                  duplicates > 0
                    ? t('phase2.playerHelpWizard.seasonForfeit.duplicatesClause', { duplicates })
                    : '',
                awardsClause:
                  awards > 0
                    ? t('phase2.playerHelpWizard.seasonForfeit.awardsClause', { awards })
                    : '',
              })}
            </Muted>
          )}
        </>
      ) : (
        <Muted>{t('phase2.playerHelpWizard.seasonForfeit.noMatchesHint')}</Muted>
      )}
      <Field>
        {t('phase2.playerHelpWizard.seasonForfeit.passwordLabel')}
        <PasswordInput
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={submitting}
        />
      </Field>
      <ForfeitBtn type="button" disabled={submitting || !password.trim()} onClick={handleForfeit}>
        {submitting
          ? t('phase2.playerHelpWizard.seasonForfeit.submitting')
          : t('phase2.playerHelpWizard.seasonForfeit.confirmButton')}
      </ForfeitBtn>
    </Box>
  );
}
